import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, has as _has } from 'lodash';
import { GlobalState } from '../ReduxState';
import { Form, FormApi, FormState } from 'informed';
import { RequestApiError, AuthentificationState, RequestApiResult } from 'loopback-react-request';
import { renderFields } from './RenderFields';
import { Button } from 'reactstrap';
import { Dispatch } from 'redux';
import { FieldType } from './InputField';
import { Redirect } from 'react-router';

export interface FormHocPropsShared {
  redirectTo?: string;
  shouldRedirect: boolean;
}

export type FormValues<Model extends {}> = {[key in keyof Model]: any};

export interface CreateFormProps<Model extends {} = {}> extends FormHocPropsShared {
  afterSubmitError?: (dispatch: Dispatch, state?: GlobalState) => void;
  afterSubmitSuccess?: (dispatch: Dispatch, state?: GlobalState) => void;
  className?: string | string[];
  dispatchData?: (dispatch: Dispatch, values: FormValues<Model>, state?: GlobalState ) => void;
  formFields: Model;
  initialValues?: 'state' | Model;
  preSubmit?: (values: FormValues<Model>, state?: GlobalState) => FormValues<Model>;
  resultKey?: string;
  state?: GlobalState;
  stateKeys: keyof GlobalState;
  submitButton?: {
    text: string;
    className?: string | string[];
  };
}

export interface FormHocProps {
  dispatch: Dispatch;
  state: GlobalState;
}

export interface FormHocState<Model extends {} = {}> extends FormHocPropsShared {
  fields: Model;
  formApi?: FormApi<FormValues<Model>>;
  formState?: FormState<FormValues<Model>>;
  hasError: boolean;
  requestError?: RequestApiError;
  submitted: boolean;
}

export interface FormWrappedComponentProps<Model extends {} = {}> extends FormHocPropsShared {
  dispatch: Dispatch;
  requestError?: RequestApiError;
  state: GlobalState;
  values?: FormValues<Model>;
}

/**
 * 
 * @param store 
 * @param key 
 */
const getStates = (store: GlobalState, key: CreateFormProps['stateKeys']): unknown | undefined => {
  if (store[key]) {
    return store[key];
  }
  return;
}

/**
 * High order component to create equal forms including error handling and dispatching 
 * data to a reducer.
 * 
 * @param {Object} props 
 * @param {string | string[])} props.className Main form CSS class name as string or 
 * array of string.
 * @param {Function} props.dispatchData (dispatch, values) => void -- Function to communicate 
 * the API and send Data.
 * @param {Object} props.formFields Form data for populating fields like Input and checkbox.
 * @param {Function} props.preSubmit (values: FormValues<Model>) => FormValues<Model>;
 * @param {string} props.resultKey Key to identify the result object of the API response
 * @param {GlobalState} props.state Whole global Redux Store
 * @param {string} props.stateKeys A key of globalState to identify the current Redux state.
 * @param {Object} props.submitButton Text and class name for the submit button.
 * @returns Function(ComponentType<FormWrappedComponentProps<Model>> | undefined ) WrappedComponent
 * 
 * @example 
 *   const form = createForm(
 *     className: 'class' || ['class'],
 *     dispatchData: (dispatch, values, state) => dispatch(callApiAction(values)),
 *     formFields: loginFormValues(),
 *     getInitialValues: true,
 *     preSubmit: (values, state) => {
 *       // Do something with values before submitting;
 *       return values;
 *     }
 *     resultKey: 'accessToken',
 *     shouldRedirect: false,
 *     stateKeys: 'authentification',
 *     submitButton: {
 *       text: 'submit',
 *       className: 'class' || ['class']
 *     }
 *   )(FormComponent);
 * 
 *   export const FormWithState = connect(state => ({
 *     state,  
 *   }))(
 *     form
 *   );
 */
export const createForm = <Model, P extends CreateFormProps<Model>>(props: P) => 

  /**
   * @param WrappedComponent {ComponentType<FormWrappedComponentProps<Model>>}
   * @returns React component class;
   */
  (WrappedComponent?: React.ComponentType<FormWrappedComponentProps<Model>>) => 

    /**
     * React High order component class to prepare data and render HTML
     */
    class FormHOC extends React.Component<FormHocProps, FormHocState<Model>> {
      
      /**
       * Creates the High order component
       * @param {Object} formHocProps 
       * @param {Function} dispatch Redux dispatch function
       * @param {Object} state Redux Global state
       * @returns {JSX.Element} Returns the rendered Form
       */
      constructor(formHocProps: FormHocProps) {
        super(formHocProps);

        this.getFormApi = this.getFormApi.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);

        this.state = {
          fields: props.formFields,
          hasError: false,
          shouldRedirect: false,
          submitted: false,
        };
        
      }

      /**
       * Check if the Form has been Submitted, then check for the Result of the dispatched 
       * request and depending on it set the success or error state.
       */
      componentDidUpdate() {
        const {
          formState,
          requestError,
          shouldRedirect,
        } = this.state;
        const data = this.result[this.resultKey];
        const status = (this.result as any).meta && (this.result as any).meta.status;
    
        /**
         * Check if Form has been Submitted, if true the count of `formState.submits` is larger then 0, 
         * proceed checking the result
         */ 
        
        if (formState && formState.submits > 0) {
          // Check if the request is finalized and any result but no error is present.
          if ((data || status === 204) && !this.result.error && this.result.loaded && !this.result.updating && !shouldRedirect) {
            if (props.afterSubmitSuccess) {
              props.afterSubmitSuccess(this.props.dispatch, this.props.state);
            }

             // Submitting Successful form should redirect
             this.setState({shouldRedirect: true});
          }

          // Check if the request is finalized and has errors.
          if (!data && this.result.error && this.result.loaded && !this.result.updating && !requestError) {
            // Error result from server, should display the error.
            this.setState({
              requestError: this.result.error,
            });

            if (props.afterSubmitError) {
              props.afterSubmitError(this.props.dispatch, this.props.state);
            }
          }
        }
      }

      /**
       * Renders the form
       */
      render() {
        const {
          fields,
          formState,
          shouldRedirect,
        } = this.state;
        
        // Prepare object for the WrappedComponent
        const wrappedProps: FormWrappedComponentProps<FormValues<Model>> = {
          dispatch: this.props.dispatch,
          requestError: this.result.error,
          redirectTo: props.redirectTo,
          shouldRedirect,
          state: this.props.state,
          values: formState && formState.values,
        }

        return (
          <Form
            getApi={this.getFormApi}
            onChange={this.handleChange}
            onSubmit={this.handleSubmit}
            initialValues={this.initialValues}
          >
            {({formState}) => (
              <div className={(props.className && classNames(props.className) || '')}>

                {WrappedComponent ? (
                  <WrappedComponent {...wrappedProps} />
                ) : (
                  <div className="row m-0">
                    <div className="col-12">
                      {this.result.error && (
                        <p className="text-danger">
                          {this.result.error.message}
                        </p>
                      )}
                      {!this.result.error && props.shouldRedirect && props.redirectTo && shouldRedirect && (
                        <Redirect to={props.redirectTo} />
                      )}
                    </div>
                  </div>
                )}

                <div className="row m-0">
                  {fields && Object.keys(fields).map((fieldKey: string, index: number) => {
                    const key = `form_field_${fieldKey}_${index}`;
                    const field = fields[fieldKey] as FieldType;
                    field.disabled = this.result.updating || field.disabled;
                    return (
                      <div key={key} className={field.col ? `col-12 col-sm-${field.col}` : 'col-12'}>
                        {renderFields<Model>({
                          field: field,
                          formState: formState,
                          index: fieldKey,
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="row m-0">
                  <div className="col-12 text-center">
                    <Button
                      className={(props.submitButton && classNames(props.submitButton.className)) || ''} 
                      type="submit"
                      disabled={this.disabled}
                    >
                      {(props.submitButton && props.submitButton.text) || 'Submit'}
                    </Button>
                  </div>
                </div>

              </div>
            )}
          </Form>
        )
      }

      private get disabled(): boolean {
        const {
          formState,
          fields,
        } = this.state;
        
        if (!formState || this.result.updating || (formState && formState.pristine) || (formState && formState.invalid)) {
          return true;
        }

        const { errors } = formState;
        const errorKeys = errors && Object.keys(errors);

        if (errorKeys && errorKeys.length > 0) {
          return true;
        }

        let disabled = false; 
        const fieldKeys: (keyof Model)[] = Object.keys(fields) as (keyof Model)[];
        fieldKeys.map(key => {
          if (!formState.values[key] && (fields[key] as any).required) {
            disabled = true;
          }
        });

        return disabled;
      }

      /**
       * Gets the `informed` form API and saves it to the component state.
       * @param formApi {FormApi} `informed` form API
       */
      private getFormApi(formApi: FormApi<FormValues<Model>>) {
        this.setState({formApi});
      }

      /**
       * Returns the initial values from store if `props.initialValues === 'state'` 
       * or from `props.initialValues = Object` if it doesn't met the conditions it 
       * will return `undefined`.
       * @returns {Object | undefined} Initial values from store or props
       */
      private get initialValues(): Model | undefined {
        const {
          initialValues,
        } = props;

        const {
          fields,
        } = this.state;

        if (initialValues && typeof initialValues !== 'string') {
          return initialValues;
        }

        if (initialValues && typeof initialValues === 'string' && initialValues === 'state') {
          const result = 
            (this.result && (this.result as RequestApiResult<Model>).result) || 
            (this.result && (this.result as AuthentificationState).accessToken) || 
            undefined;

          if (!result) {
            return; 
          }

          const keys = Object.keys(fields) as (keyof Model)[];
          const values: FormValues<Model> | {} = {};          
          keys.map(key => {
            if (_has(result, key)) {
              (values as FormValues<Model>)[key] = (result as Model)[key];
            }
          });
          return values as Model;
        }

        return;
      }

      /**
       * Handles the change of input values, checks for errors and saves them to 
       * the component state,
       * @param formState {FormState}
       * @returns void
       */
      private handleChange(formState: FormState<FormValues<Model>>) {
        const {errors} = formState;
        const errorKeys = errors && Object.keys(errors);
        const hasError = errorKeys && errorKeys.length > 0 ? true : false;

        this.setState({
          formState,
          hasError,
        });
      }

      /**
       * Handles the form submit, checks for values, errors and calls the dispatch callback
       * @returns void
       */
      private handleSubmit() {
        if (this.result.updating) {
          return;
        }

        const {
          formApi,
          formState,
          hasError,
        } = this.state;

        let values = formState && formState.values;

        if (values && !hasError && !this.disabled) {
          if (props.preSubmit) {
            values = props.preSubmit(values, this.props.state);
            if (formApi) {
              formApi.setValues(values);
            }
          }

          if (props.dispatchData) {
            props.dispatchData(this.props.dispatch, values, this.props.state);
            if (formState && formState.submits > 0) {
              this.setState({
                shouldRedirect: false,
                requestError: undefined,
              });
            }
          }
        }
      }

      /**
       * Save the `resultKey` this class
       * @returns {string}
       */
      private get resultKey(): string {
        return props.resultKey || 'result';
      }

      /**
       * Save the API `result` this class
       * @returns {RequestApiResult<Model> | AuthentificationState}
       */
      private get result(): RequestApiResult<Model> | AuthentificationState {
        const result: RequestApiResult<Model> | AuthentificationState = {
          loaded: false,
          updating: false
        };
        if (this.resultKey === 'accessToken') {
          const store = this.store as AuthentificationState;
          return _assign({}, result, store);
        } else {
          const store = this.store as RequestApiResult<Model>;
          return _assign({}, result, store,);
        }
      }

      /** 
       * Save the redux store to this class
       * @returns {Object | undefined}
       */
      private get store(): unknown | undefined {
        const {
          state
        } = this.props;
        
        return state && getStates(state, props.stateKeys);
      }
    };

    



export default createForm;

